import { Box, Snackbar, Alert, TextField } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";

interface PediatricTraumaScoreFormData {
  PTS_ID: string;
  CTIME: Date;
  PROCESS_TIME: Date;
  CUSER: string;
  HEIGHT: string;
  AIRWAY_STATUS: string;
  CENTRAL_NERV_SYSTEM: string;
  SISTOLIC_BP: string;
  OPEN_WOUND: string;
  SKELETAL_INJURY: string;
  NURSE_NOTE: string;
  PTS_SCORE: string;
}

interface PediatricTraumaScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof PediatricTraumaScoreFormData, value: any) => void;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function PediatricTraumaScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  isSaving,
  extraAccess,
}: PediatricTraumaScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "PEDIATRIC_TRAUMA_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof PediatricTraumaScoreFormData, value);
      });
    },
  });

  const typedFormData = formData as PediatricTraumaScoreFormData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof PediatricTraumaScoreFormData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  useEffect(() => {
    if (!typedFormData) return;

    const scoreFields = [
      "HEIGHT_XVALUE",
      "AIRWAY_STATUS_XVALUE",
      "CENTRAL_NERV_SYSTEM_XVALUE",
      "SISTOLIC_BP_XVALUE",
      "OPEN_WOUND_XVALUE",
      "SKELETAL_INJURY_XVALUE",
    ];

    const score = scoreFields.reduce((sum, field) => {
      const value = Number((typedFormData as any)[field]) || 0;
      return sum + value;
    }, 0);

    setScore(score);
    handleChange("PTS_SCORE", score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typedFormData?.HEIGHT,
    typedFormData?.AIRWAY_STATUS,
    typedFormData?.CENTRAL_NERV_SYSTEM,
    typedFormData?.SISTOLIC_BP,
    typedFormData?.OPEN_WOUND,
    typedFormData?.SKELETAL_INJURY,
  ]);


  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
            <OtherPrivilegeGuard
              extraAccess={!!extraAccess}
              fallback={
                <ByzMuiSaveButton
                  disabled
                  tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
                />
              }
            >
              <ByzMuiSaveButton
                onClick={() => save(typedFormData.PTS_ID)}
                loading={isSaving}
              />
            </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("pediatric-trauma-score.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("pediatric-trauma-score.score-calculation")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pediatric-trauma-score.height")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pediatric-trauma-score.height")}
                  mcpId={cardData.mcpConfig?.HEIGHT?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("HEIGHT", selected);
                    }
                  }}
                  value={selectedMCPs.HEIGHT}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.HEIGHT?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pediatric-trauma-score.airway-status")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pediatric-trauma-score.airway-status")}
                  mcpId={cardData.mcpConfig?.AIRWAY_STATUS?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("AIRWAY_STATUS", selected);
                    }
                  }}
                  value={selectedMCPs.AIRWAY_STATUS}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.AIRWAY_STATUS?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pediatric-trauma-score.central-nerv-system")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pediatric-trauma-score.central-nerv-system")}
                  mcpId={cardData.mcpConfig?.CENTRAL_NERV_SYSTEM?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("CENTRAL_NERV_SYSTEM", selected);
                    }
                  }}
                  value={selectedMCPs.CENTRAL_NERV_SYSTEM}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.CENTRAL_NERV_SYSTEM?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pediatric-trauma-score.sistolic-bp")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pediatric-trauma-score.sistolic-bp")}
                  mcpId={cardData.mcpConfig?.SISTOLIC_BP?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("SISTOLIC_BP", selected);
                    }
                  }}
                  value={selectedMCPs.SISTOLIC_BP}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.SISTOLIC_BP?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pediatric-trauma-score.open-wound")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pediatric-trauma-score.open-wound")}
                  mcpId={cardData.mcpConfig?.OPEN_WOUND?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("OPEN_WOUND", selected);
                    }
                  }}
                  value={selectedMCPs.OPEN_WOUND}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.OPEN_WOUND?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pediatric-trauma-score.skeletal-injury")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("pediatric-trauma-score.skeletal-injury")}
                  mcpId={cardData.mcpConfig?.SKELETAL_INJURY?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("SKELETAL_INJURY", selected);
                    }
                  }}
                  value={selectedMCPs.SKELETAL_INJURY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.SKELETAL_INJURY?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("pediatric-trauma-score.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("note")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <TextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={typedFormData.NURSE_NOTE}
                  onChange={(e) => handleChange("NURSE_NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("pediatric-trauma-score.pts-score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default PediatricTraumaScoreCreate;
